<script setup>
// A gaveta de criar e editar campanha (D-G6): um invólucro fino sobre o `EditorDrawer` do kit que escolhe o formulário
// pelo tipo. Só o chat ao vivo edita (8-3 = A). Criar/Salvar valida no formulário e grava pelo store, sem mudar nada
// do woot; a gaveta fecha ao gravar com sucesso (com erro fica aberta, para não perder o que foi escrito).
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';

import EditorDrawer from 'dashboard/components-next/polaris/cards/EditorDrawer.vue';
import LiveChatCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/LiveChatCampaign/LiveChatCampaignForm.vue';
import SMSCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/SMSCampaign/SMSCampaignForm.vue';
import WhatsAppCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignForm.vue';

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: value => ['live-chat', 'sms', 'whatsapp'].includes(value),
  },
  // A campanha em edição; sem ela a gaveta cria.
  campaign: {
    type: Object,
    default: null,
  },
});

const open = defineModel('open', { type: Boolean, default: false });

const { t } = useI18n();
const store = useStore();

const drawerRef = ref(null);
const formRef = ref(null);
const title = ref('');

const isEdit = computed(() => Boolean(props.campaign?.id));
const isLiveChat = computed(() => props.type === 'live-chat');

// O campo do cabeçalho começa com o título da campanha (ou vazio, ao criar) toda vez que a gaveta abre ou troca de alvo.
watch(
  [open, () => props.campaign],
  () => {
    title.value = props.campaign?.title ?? '';
  },
  { flush: 'sync' }
);

const kind = computed(() => {
  const kinds = {
    'live-chat': t('CAMPAIGN.DRAWER.KIND.LIVE_CHAT'),
    sms: t('CAMPAIGN.DRAWER.KIND.SMS'),
    whatsapp: t('CAMPAIGN.DRAWER.KIND.WHATSAPP'),
  };
  return kinds[props.type];
});

const titlePlaceholder = computed(() => {
  const placeholders = {
    'live-chat': t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.TITLE.PLACEHOLDER'),
    sms: t('CAMPAIGN.SMS.CREATE.FORM.TITLE.PLACEHOLDER'),
    whatsapp: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER'),
  };
  return placeholders[props.type];
});

const saveLabel = computed(() => {
  if (isEdit.value) return '';
  const labels = {
    'live-chat': t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.BUTTONS.CREATE'),
    sms: t('CAMPAIGN.SMS.CREATE.FORM.BUTTONS.CREATE'),
    whatsapp: t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE'),
  };
  return labels[props.type];
});

// O selo de estado só existe onde a campanha tem estado que o formulário controla (chat ao vivo: ativada ou não).
const status = computed(() => {
  if (!isLiveChat.value || !formRef.value) return null;
  return formRef.value.state.enabled
    ? {
        label: t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED'),
        variant: 'success',
      }
    : {
        label: t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED'),
        variant: 'secondary',
      };
});

const dirty = computed(() => Boolean(formRef.value?.isDirty));
const canSave = computed(() => !formRef.value?.isSaving);

const messages = computed(() => {
  const created = {
    'live-chat': {
      success: t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.API.SUCCESS_MESSAGE'),
      error: t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.API.ERROR_MESSAGE'),
    },
    sms: {
      success: t('CAMPAIGN.SMS.CREATE.FORM.API.SUCCESS_MESSAGE'),
      error: t('CAMPAIGN.SMS.CREATE.FORM.API.ERROR_MESSAGE'),
    },
    whatsapp: {
      success: t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.SUCCESS_MESSAGE'),
      error: t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE'),
    },
  };
  if (!isEdit.value) return created[props.type];
  return {
    success: t('CAMPAIGN.LIVE_CHAT.EDIT.FORM.API.SUCCESS_MESSAGE'),
    error: t('CAMPAIGN.LIVE_CHAT.EDIT.FORM.API.ERROR_MESSAGE'),
  };
});

const handleSubmit = async campaignDetails => {
  try {
    if (isEdit.value) {
      await store.dispatch('campaigns/update', {
        id: props.campaign.id,
        ...campaignDetails,
      });
    } else {
      await store.dispatch('campaigns/create', campaignDetails);

      // tracking this here instead of the store to track the type of campaign
      useTrack();
    }
    useAlert(messages.value.success);
    open.value = false;
  } catch (error) {
    useAlert(error?.response?.message || messages.value.error);
  }
};

const save = () => formRef.value?.submit();

// Cada vez que a página abre a gaveta (criar ou editar) o formulário começa de novo, mesmo quando o alvo não muda:
// "Descartar" em "Criar campanha" com a criação já aberta, ou no título da mesma linha, joga fora o que foi escrito.
const session = ref(0);
const restart = () => {
  session.value += 1;
  title.value = props.campaign?.title ?? '';
};

// A página troca de campanha com a gaveta aberta pela mesma guarda do fechar ("Descartar alterações?").
defineExpose({
  requestClose: () => drawerRef.value?.requestClose(),
  runGuarded: action =>
    drawerRef.value?.runGuarded(() => {
      action();
      restart();
    }),
});
</script>

<template>
  <EditorDrawer
    ref="drawerRef"
    v-model:open="open"
    v-model:title="title"
    :kind="kind"
    :title-placeholder="titlePlaceholder"
    :status="status"
    :dirty="dirty"
    :can-save="canSave"
    :save-label="saveLabel"
    data-campaign-drawer
    @save="save"
  >
    <LiveChatCampaignForm
      v-if="isLiveChat"
      ref="formRef"
      :key="`${campaign?.id ?? 'new'}-${session}`"
      v-model:title="title"
      :mode="isEdit ? 'edit' : 'create'"
      :selected-campaign="campaign ?? {}"
      @submit="handleSubmit"
    />
    <SMSCampaignForm
      v-else-if="type === 'sms'"
      ref="formRef"
      :key="session"
      v-model:title="title"
      @submit="handleSubmit"
    />
    <WhatsAppCampaignForm
      v-else
      ref="formRef"
      :key="session"
      v-model:title="title"
      @submit="handleSubmit"
    />
  </EditorDrawer>
</template>
